import React, { useState } from 'react';
import { connect } from 'react-redux';
import QueryString from 'query-string';
import ReactTable from 'react-table';
import i18next from 'i18next';

import { Message } from '../Message';
import { PageTitle } from '../PageTitle';
import { RowButton } from '../RowButton';
import { ScrollModule } from '../ScrollModule';
import ScrollArea from 'react-scrollbar';
import { ThresholdsService } from '../../services';
import { AccessHelper, BaseTable, FormatHelper } from '../../helpers';

import config from "config";

const DEFAULT_SORTED = [{ id: "name", desc: false }];

const Thresholds = ({ me, history, location }) => {
  const [data, setData] = useState([]);
  const [pageCount, setPageCount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [totalElements, setTotalElements] = useState(null);
  const [forcePage, setForcePage] = useState(null);
  const [localErrorMessage, setLocalErrorMessage] = useState(null);

  const viewAllowed = AccessHelper.userHasAccess(me.availableRoutes, '/config/thresholds/view/:thresholdId');
  const editAllowed = AccessHelper.userHasAccess(me.availableRoutes, '/config/thresholds/edit/:thresholdId');
  const parsedOptions = QueryString.parse(location.search);
  const currentHistoryCount = history.location?.state?.currentHistoryCount ?? 0;
  
  const fetchData = (tableData) => {
      const options = BaseTable.buildURLOptions(tableData, DEFAULT_SORTED);
      const params = options.map(option => Object.keys(option).map((k, i) => `${k}=${option[k]}`)).join('&');
      const currentSearch = location.search ? location.search : '?';

      if (currentHistoryCount < 100 && `?${params}` !== currentSearch) {
        history.push({ search: `?${params}`, state: { currentHistoryCount: currentHistoryCount + 1 } });
      } else {
        setLoading(true);
        
        let fetchOptions = BaseTable.buildFetchDataOptions(parsedOptions, DEFAULT_SORTED);

        ThresholdsService.getAll(fetchOptions, true)
            .then((response) => {
                const pageCount = Math.ceil(response.page.totalElements / tableData.pageSize);

                setData(response.thresholds);
                setPageCount(pageCount);
                setForcePage(tableData.page >= pageCount ? pageCount - 1 : null);
                setTotalElements(response.page.totalElements);
                setLoading(false);
            })
            .catch((error) => {
                console.error(error);
                setLocalErrorMessage(error.message);
            });
      }
    };

    return (
        <div className="animated fadeIn">
          <div className="row">
            <PageTitle 
              title={i18next.t('pages.thresholds.header')}
              addLinkWithParams={{ link: { pathname: "/config/thresholds/add", state: { backLinkSearch: location.search } } }} 
            />
          </div>

          <DisplayMessages localErrorMessage={localErrorMessage} location={location} onClearError={() => setLocalErrorMessage(null)} />

          <div className="op-content-box">

            <ScrollModule/>

            <ScrollArea speed={0.8} className="scroll-container" contentClassName="scrollable-content" horizontal={true} >
              <div className="row">
                <div className="col-sm-12">
                  <div className="card">
                    <div className="card-block">
                      <div className="react-table-custom">
                        <ThresholdTable
                            data={data}
                            pageCount={pageCount}
                            loading={loading}
                            totalElements={totalElements}
                            viewAllowed={viewAllowed}
                            editAllowed={editAllowed}
                            parsedOptions={parsedOptions}
                            forcePage={forcePage}
                            fetchData={fetchData}
                            location={location}
                          />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </ScrollArea>
          </div>
        </div>
    );
};

const ThresholdTable = ({data, pageCount, loading, totalElements, viewAllowed, editAllowed, parsedOptions, forcePage, fetchData, location}) => {
  const dynamicOptions = BaseTable.buildDynamicOptions(parsedOptions, forcePage, DEFAULT_SORTED);

  return (
      <ReactTable
          className="-striped -highlight"
          manual // Forces table not to paginate or sort automatically, so we can handle it server-side
          data={data}
          pages={pageCount} // Display the total number of pages
          loading={loading} // Display the loading overlay when we need it
          totalElements={totalElements}
          onFetchData={fetchData} // Request new data when things change
          showPaginationTop
          columns={[
              {
                  Header: i18next.t("table-headers.name"),
                  accessor: "name",
                  sortable: true,
                  minResizeWidth: 50,
              },
              {
                  Header: i18next.t("table-headers.measurement"),
                  Cell: (cell) => <MeasureRowView threshold={cell.original} />,
                  sortable: false,
                  minResizeWidth: 50,
              },
              {
                  Cell: (cell) => (
                      <RowButton
                          allowView={viewAllowed}
                          allowEdit={editAllowed}
                          viewPath={{
                              pathname: `/config/thresholds/view/${cell.original.id}`,
                              state: { backLinkSearch: location.search }
                          }}
                          editPath={{
                              pathname: `/config/thresholds/edit/${cell.original.id}`,
                              state: { backLinkSearch: location.search }
                          }}
                      />
                  ),
                  sortable: false,
                  minResizeWidth: 50,
                  width: 150,
              },
          ]}
          {...dynamicOptions}
      />
  );
}

const DisplayMessages = ({localErrorMessage, location, onClearError}) => {
  return (
    <div>
      { localErrorMessage && <Message errorMessage={localErrorMessage} messageDismissCallback={onClearError} /> }
      { location.state && <Message messages={location} /> }
    </div>
  );
}

const MeasureRowView = ({threshold}) => {
    var description = "Unknown";
    if (threshold.deviceDataType) { 
      description = i18next.t(`device-data-types.${threshold.deviceDataType.token}`);
    } else if (threshold.deviceDataClassification) {
      description = i18next.t(`device-data-classifications.${threshold.deviceDataClassification.token}`);
    }

    return (
        <span>
            <img src={`${config.WEB_BASE_URL}/images/icon/sensor_${threshold.thresholdClassification.token}.svg`} height="16" alt={i18next.t("measurement-icon")} />
            &nbsp;
            {description}
            &nbsp;
            <span className="threshold-evaluation">{threshold.thresholdEvaluationType.shortForm}</span>
            &nbsp;
            {FormatHelper.formatMeasurement(threshold)}
        </span>
    );
};

export default connect((state) => ({ me: state.user.me }))(Thresholds);